import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  MapPin, 
  Star, 
  Phone, 
  Mail, 
  Users, 
  Calendar, 
  Clock, 
  ChefHat, 
  Utensils, 
  Award,
  ArrowLeft,
  Heart,
  Share2,
  MessageCircle,
  CheckCircle,
  Clock as ClockIcon,
  DollarSign,
  Users as UsersIcon,
  Send,
  Loader2,
  LayoutGrid,
} from 'lucide-react';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import {
  getRestaurantById,
  getRestaurantDetails,
  getRestaurantReviews,
  getRestaurantReviewsPublic,
  createRestaurantReview,
  getDishesByRestaurant,
} from '../services/api';
import { getCateringServices, createCateringBooking } from '../services/cateringApi';
import { useFavoriteAuth } from '../hooks/useFavoriteAuth';
import LoginPopup from '../components/LoginPopup';
import {
  getCateringServiceTypeLabel,
  getActiveDietaryLabels,
  CATERING_OCCASIONS,
  CATERING_PAID_OPTIONS,
  DIETARY_OPTIONS,
  computeCateringQuote,
  getOfferStartingPrice,
} from '../constants/catering';

const formatPrice = (amount) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);

const labelForServiceType = getCateringServiceTypeLabel;

const CateringServiceDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [cateringServices, setCateringServices] = useState([]);
  const [restaurantDishes, setRestaurantDishes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    review_text: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [topOffset, setTopOffset] = useState(80); // Navbar par défaut
  
  // Hook pour gérer les favoris et l'authentification
  const {
    isAuthenticated,
    showLoginPopup,
    handleFavoriteAction,
    handleReviewAction,
    handleLoginSuccess,
    closeLoginPopup,
    checkFavoriteStatus,
    loading: favoriteLoading
  } = useFavoriteAuth();

  // État pour suivre si on doit ouvrir le formulaire après connexion
  const [shouldOpenReviewFormAfterLogin, setShouldOpenReviewFormAfterLogin] = useState(false);
  
  const [isFavorite, setIsFavorite] = useState(false);
  const emptyContactForm = (serviceId = null) => ({
    serviceId,
    email: '',
    phone: '',
    occasion: '',
    date: '',
    time: '',
    guests: '',
    location: '',
    message: '',
    options: { delivery: false, setup: false, equipment: false, staff_hours: 0 },
    dietary: [],
  });
  const [contactForm, setContactForm] = useState(emptyContactForm());
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');
  const [selectedDish, setSelectedDish] = useState(null);

  useEffect(() => {
    fetchCateringServiceDetails();
  }, [id]);

  // Calculer la hauteur de la navbar et de la bande de promotion
  useEffect(() => {
    const updateOffset = () => {
      const navbar = document.querySelector('nav') || document.querySelector('[class*="z-50"]');
      const banner = document.querySelector('[class*="z-40"][style*="top"]');
      
      let totalHeight = 80; // Hauteur navbar par défaut
      
      if (navbar) {
        totalHeight = navbar.offsetHeight;
      }
      
      if (banner) {
        totalHeight += banner.offsetHeight || 60; // Hauteur bande par défaut
      }
      
      setTopOffset(totalHeight);
    };
    
    updateOffset();
    window.addEventListener('resize', updateOffset);
    
    // Attendre que le DOM soit chargé
    setTimeout(updateOffset, 100);
    
    return () => window.removeEventListener('resize', updateOffset);
  }, []);

  // Vérifier le statut favori au chargement
  useEffect(() => {
    if (isAuthenticated && id) {
      checkFavoriteStatus(id, 'restaurant').then(status => {
        setIsFavorite(status);
      });
    }
  }, [isAuthenticated, id, checkFavoriteStatus]);

  const fetchCateringServiceDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const restaurantId = Number(id);
      if (!id || Number.isNaN(restaurantId)) {
        setError(t('cateringServiceDetails.invalidRestaurantId'));
        return;
      }

      const restaurantData = await getRestaurantById(restaurantId);

      let profile = null;
      try {
        const details = await getRestaurantDetails(restaurantId);
        profile = details.profile || details;
      } catch (e) {
        console.warn('Détails / profil restaurant non chargés', e);
      }

      const servicesData = await getCateringServices({
        restaurant_id: restaurantId,
        is_active: true,
      }).catch(() => []);

      if (!servicesData || servicesData.length === 0) {
        setError(t('cateringServiceDetails.noServicesOffered'));
        setRestaurant({ ...restaurantData, profile });
        setCateringServices([]);
        setRestaurantDishes([]);
        setReviews([]);
        return;
      }

      let reviewsData = [];

      try {
        const rr = await getRestaurantReviews(restaurantId);
        if (Array.isArray(rr) && rr.length > 0) {
          reviewsData = rr.map((review) => ({
            id: `r-${review.id}`,
            customer_id: review.user_id,
            restaurant_id: restaurantId,
            rating: review.rating,
            review_text: review.comment,
            created_at: review.created_at || new Date().toISOString(),
            user: review.user || null,
            customer: review.user ? { name: review.user.name || t('cateringServiceDetails.anonymousUser') } : null,
          }));
        }
      } catch (e) {
        console.warn('Avis restaurant (legacy) non chargés', e);
      }

      try {
        const cr = await getRestaurantReviewsPublic(restaurantId);
        if (Array.isArray(cr) && cr.length > 0) {
          const converted = cr.map((review) => ({
            id: `c-${review.id}`,
            customer_id: review.client_profile_id,
            restaurant_id: restaurantId,
            rating: review.rating ?? 5,
            review_text: review.review_text || review.comment || '',
            created_at: review.created_at || new Date().toISOString(),
            user: null,
            customer: review.user_name ? { name: review.user_name } : { name: t('cateringServiceDetails.anonymousUser') },
          }));
          const seen = new Set(reviewsData.map((x) => x.id));
          converted.forEach((r) => {
            if (!seen.has(r.id)) {
              seen.add(r.id);
              reviewsData.push(r);
            }
          });
        }
      } catch (e) {
        console.warn('Avis publics non chargés', e);
      }

      let dishesList = [];
      try {
        const rawDishes = await getDishesByRestaurant(restaurantId, true);
        // Onglet "Plats" du service traiteur : uniquement les plats marqués service traiteur.
        dishesList = (Array.isArray(rawDishes) ? rawDishes : []).filter((d) => d.catering_only === true);
      } catch (e) {
        console.warn('Plats du restaurant non chargés', e);
      }

      setRestaurant({ ...restaurantData, profile });
      setCateringServices(servicesData);
      setRestaurantDishes(dishesList);
      setReviews(reviewsData);
    } catch (err) {
      const msg =
        err?.message ||
        (typeof err === 'string' ? err : t('cateringServiceDetails.loadError'));
      setError(msg);
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour transformer les données en format attendu par le template
  const getFormattedData = () => {
    if (!restaurant || !cateringServices.length) return null;

    const mainService = cateringServices[0];
    const minCapacity = Math.min(...cateringServices.map((s) => s.min_guests || 1));
    const maxVals = cateringServices.map((s) => s.max_guests).filter((v) => v != null);
    const maxCapacity = maxVals.length ? Math.max(...maxVals) : minCapacity + 100;

    // Prix d'appel : la moins chère des offres (prix/pers. en priorité), pas une moyenne
    const startingPrices = cateringServices.map(getOfferStartingPrice);
    const perPersonPrices = startingPrices.filter((p) => p.perPerson).map((p) => p.amount);
    const startingPrice = perPersonPrices.length
      ? { amount: Math.min(...perPersonPrices), perPerson: true }
      : { amount: Math.min(...startingPrices.map((p) => p.amount)), perPerson: false };

    const eventTypes = [
      ...new Set(cateringServices.map((s) => labelForServiceType(s.service_type))),
    ];

    const menus = cateringServices.map((service) => ({
      id: service.id,
      name: service.service_name,
      price: getOfferStartingPrice(service).amount,
      perPerson: getOfferStartingPrice(service).perPerson,
      description:
        service.service_description || t('cateringServiceDetails.defaultServiceDescription'),
      image_url: service.image_url || null,
      courses: [
        service.delivery_available ? t('cateringServiceDetails.deliveryAvailable') : null,
        service.setup_available ? t('cateringServiceDetails.setupIncluded') : null,
        service.staff_available ? t('cateringServiceDetails.staffService') : null,
        service.equipment_rental ? t('cateringServiceDetails.equipmentRental') : null,
      ].filter(Boolean),
    }));

    // Formater les avis avec plus de détails
    const formattedReviews = reviews.map(review => ({
      id: review.id,
      author: review.customer?.name || review.user?.name || t('cateringServiceDetails.clientNumber', { id: review.customer_id || review.user_id }),
      rating: review.rating || 5,
      date: review.created_at ? new Date(review.created_at).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) : new Date().toLocaleDateString('fr-FR'),
      comment: review.review_text || review.comment || t('cateringServiceDetails.defaultReviewComment'),
      customer_id: review.customer_id || review.user_id
    }));

    return {
      id: restaurant.id,
      name: restaurant.name,
      description: restaurant.description || mainService.service_description || t('cateringServiceDetails.defaultProfessionalService'),
      rating: Number(restaurant.rating || mainService.rating || 0),
      reviewCount: mainService.review_count || reviews.length || 0,
      depositPercentage: Number(mainService.deposit_percentage ?? 30),
      location: t('cateringServiceDetails.locationInFrance', { city: restaurant.city }),
      address: restaurant.address,
      phone: restaurant.phone,
      email: restaurant.email,
      website: restaurant.profile?.website_url || null,
      cuisine: restaurant.cuisine_type || t('cateringServiceDetails.defaultCuisine'),
      specialties: [
        restaurant.cuisine_type,
        labelForServiceType(mainService.service_type),
      ].filter(Boolean),
      eventTypes: eventTypes,
        capacity: {
        min: minCapacity,
        max: maxCapacity
        },
        pricing: {
        startingPrice,
          currency: "€",
        includes: [
          mainService.setup_available ? t('cateringServiceDetails.setup') : null,
          mainService.staff_available ? t('cateringServiceDetails.staffService') : null,
          mainService.equipment_rental ? t('cateringServiceDetails.equipmentProvided') : null,
          mainService.delivery_available ? t('cateringServiceDetails.deliveryAvailable') : null
        ].filter(Boolean),
        extras: (() => {
          // Construire les extras depuis les services disponibles
          const extras = [];
          // Vérifier tous les services pour les extras disponibles
          cateringServices.forEach(service => {
            if (service.equipment_rental && service.equipment_fee && parseFloat(service.equipment_fee) > 0) {
              extras.push(t('cateringServiceDetails.extraEquipmentRental', { price: parseFloat(service.equipment_fee) }));
            }
            if (service.staff_available && service.staff_fee_per_hour && parseFloat(service.staff_fee_per_hour) > 0) {
              extras.push(t('cateringServiceDetails.extraStaff', { price: parseFloat(service.staff_fee_per_hour) }));
            }
            if (service.delivery_available && service.delivery_fee && parseFloat(service.delivery_fee) > 0) {
              extras.push(t('cateringServiceDetails.extraDelivery', { price: parseFloat(service.delivery_fee) }));
            }
            if (service.setup_available && service.setup_fee && parseFloat(service.setup_fee) > 0) {
              extras.push(t('cateringServiceDetails.extraSetup', { price: parseFloat(service.setup_fee) }));
            }
          });
          // Retourner les extras uniques
          return [...new Set(extras)];
        })()
        },
        services: {
        setupTime: `${mainService.preparation_time_hours || 24}h`,
        staffIncluded: mainService.staff_available || false,
        equipmentProvided: mainService.equipment_rental || false,
        dietaryAccommodations: getActiveDietaryLabels(mainService.dietary_options),
      },
      menus: menus,
      reviews: formattedReviews,
      images: (() => {
        const imgs = [];
        if (restaurant.profile?.banner_image_url) imgs.push(restaurant.profile.banner_image_url);
        cateringServices.forEach((s) => {
          if (s.image_url && !imgs.includes(s.image_url)) imgs.push(s.image_url);
        });
        return imgs;
      })(),
      /** Certifications saisies par le restaurateur (ex: Certifié Halal) — vide si non renseignées */
      certifications: [
        ...new Set(cateringServices.flatMap((s) => (Array.isArray(s.certifications) ? s.certifications : []))),
      ],
      experience: (() => {
        if (restaurant.created_at) {
          const years = new Date().getFullYear() - new Date(restaurant.created_at).getFullYear();
          return t('cateringServiceDetails.yearsExperience', { count: years });
        }
        return t('cateringServiceDetails.defaultProfessionalServiceShort');
      })(),
      /** Plats à la carte du restaurant (API /dishes/restaurant) */
      dishes: (restaurantDishes || [])
        .filter((d) => d.is_available !== false)
        .map((d) => ({
          id: d.id,
          name: d.name,
          price: Number(d.price) || 0,
          description: typeof d.description === 'string' ? d.description : '',
          imageUrl: d.dish_image_url || '',
        })),
    };
  };
  
  const cateringService = getFormattedData();

  // Offre sur laquelle porte la demande (celle choisie, sinon la première)
  const selectedOffer =
    cateringServices.find((s) => s.id === contactForm.serviceId) || cateringServices[0] || null;

  // La demande traiteur est rattachée au compte client : connexion obligatoire
  const openQuoteModal = (serviceId = null) => {
    handleReviewAction('openQuoteForm', () => {
      setContactForm(emptyContactForm(serviceId ?? cateringServices[0]?.id ?? null));
      setShowContactModal(true);
    });
  };

  const setContactOption = (key, value) => {
    setContactForm((f) => ({ ...f, options: { ...f.options, [key]: value } }));
  };

  const toggleContactDietary = (dietId) => {
    setContactForm((f) => ({
      ...f,
      dietary: f.dietary.includes(dietId) ? f.dietary.filter((d) => d !== dietId) : [...f.dietary, dietId],
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactError('');
    setContactLoading(true);
    try {
      const bookingData = {
        service_id: selectedOffer?.id || null,
        occasion: contactForm.occasion || null,
        event_date: contactForm.date,
        event_time: contactForm.time ? `${contactForm.time}:00` : '12:00:00',
        guest_count: parseInt(contactForm.guests, 10),
        contact_email: contactForm.email,
        contact_phone: contactForm.phone || null,
        delivery_address: contactForm.location || null,
        special_requests: contactForm.message || null,
        options: contactForm.options,
        dietary_requirements: contactForm.dietary.length ? contactForm.dietary : null,
      };
      await createCateringBooking(bookingData);
      setContactSuccess(true);
    } catch (err) {
      setContactError(err.message || t('cateringServiceDetails.genericError'));
    } finally {
      setContactLoading(false);
    }
  };

  const closeContactModal = () => {
    setShowContactModal(false);
    setContactSuccess(false);
    setContactError('');
    setContactForm(emptyContactForm());
  };

  const handleInputChange = (e) => {
    setContactForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  // Même calcul que l'API : le montant affiché est celui enregistré pour le traiteur
  const getModalEstimate = () => computeCateringQuote(selectedOffer, contactForm.guests, contactForm.options);

  const toggleFavorite = async () => {
    const action = isFavorite ? 'remove' : 'add';
    const success = await handleFavoriteAction(action, parseInt(id), 'restaurant');
    if (success) {
    setIsFavorite(!isFavorite);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = cateringService?.name || t('cateringServiceDetails.shareDefaultTitle');
    const text = cateringService?.description || t('cateringServiceDetails.shareDefaultText');

    try {
      // Essayer l'API Web Share si disponible (mobile)
      if (navigator.share) {
        await navigator.share({
          title: title,
          text: text,
          url: url,
        });
      } else {
        // Fallback: copier dans le presse-papier
        await navigator.clipboard.writeText(url);
        alert(t('cateringServiceDetails.linkCopied'));
      }
    } catch (error) {
      // L'utilisateur a annulé le partage ou une erreur s'est produite
      if (error.name !== 'AbortError') {
        // Si ce n'est pas une annulation, essayer de copier dans le presse-papier
        try {
          await navigator.clipboard.writeText(url);
          alert(t('cateringServiceDetails.linkCopied'));
        } catch (clipboardError) {
          console.error('Erreur lors de la copie:', clipboardError);
          // Dernier recours: afficher l'URL
          prompt(t('cateringServiceDetails.copyThisLink'), url);
        }
      }
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      alert(t('cateringServiceDetails.pleaseLoginToReview'));
      return;
    }

    setSubmittingReview(true);
    try {
      await createRestaurantReview(Number(id), {
        rating: reviewForm.rating,
        comment: reviewForm.review_text,
      });

      setReviewForm({ rating: 5, review_text: '' });
      setShowReviewForm(false);

      await fetchCateringServiceDetails();
    } catch (error) {
      console.error('Erreur lors de la soumission de l\'avis:', error);
      alert(t('cateringServiceDetails.reviewSubmitError'));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleReviewInputChange = (e) => {
    setReviewForm({
      ...reviewForm,
      [e.target.name]: e.target.value
    });
  };

  const handleRatingChange = (rating) => {
    setReviewForm({
      ...reviewForm,
      rating
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-namke-white">
        <NavBar />
        <div className="animate-pulse">
          {/* Hero image */}
          <div className="w-full h-56 sm:h-72 bg-namke-cream" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {/* 2-col info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-3">
                <div className="h-7 bg-namke-cream rounded-full w-2/3" />
                <div className="h-4 bg-namke-cream rounded-full w-full" />
                <div className="h-4 bg-namke-cream rounded-full w-5/6" />
                <div className="flex gap-2 mt-2">
                  <div className="h-10 w-32 bg-namke-cream rounded-full" />
                  <div className="h-10 w-32 bg-namke-cream rounded-full" />
                </div>
              </div>
              <div className="bg-white rounded-2xl p-4 space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-4 bg-namke-cream rounded-full w-3/4" />
                ))}
              </div>
            </div>
            {/* Tab bar */}
            <div className="flex gap-3 border-b border-namke-black/10 pb-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-8 w-20 bg-namke-cream rounded-full" />
              ))}
            </div>
            {/* Tab content cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-4 space-y-2">
                  <div className="h-32 bg-namke-cream rounded-2xl" />
                  <div className="h-4 bg-namke-cream rounded-full w-2/3" />
                  <div className="h-3 bg-namke-cream rounded-full w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !cateringService) {
    return (
      <div className="min-h-screen bg-namke-white">
        <NavBar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="text-namke-terracotta mb-4">
              <MessageCircle size={48} className="mx-auto" />
            </div>
            <h2 className="text-xl font-semibold text-namke-black mb-2">{t('cateringServiceDetails.errorTitle')}</h2>
            <p className="text-namke-black/60 mb-4">{error || t('cateringServiceDetails.notFound')}</p>
            <button
              onClick={() => navigate('/traiteur')}
              className="bg-[#054625] text-white px-6 py-2 rounded-full hover:bg-[#04381f] transition-colors"
            >
              {t('cateringServiceDetails.backToCateringServices')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-namke-white">
      <NavBar />
      
      {/* Header avec fond blanc */}
      <div className="relative bg-white min-h-[300px] md:min-h-[400px]" style={{ paddingTop: `${topOffset + 20}px` }}>
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-8 md:py-16">
          <button
            onClick={() => navigate('/traiteur')}
            className="inline-flex items-center text-namke-black/65 hover:text-namke-black mb-6 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('cateringServiceDetails.backToCateringServices')}
          </button>

          {cateringService.images?.length > 0 && (
            <div className="w-full mb-8 rounded-3xl overflow-hidden bg-namke-cream">
              <img
                src={cateringService.images[0]}
                alt={cateringService.name}
                className="w-full h-48 sm:h-56 md:h-72 object-cover"
              />
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4 leading-tight" style={{ color: 'rgb(5, 70, 37)' }}>
                {cateringService.name}
              </h1>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-namke-black/70 mb-3 md:mb-6 leading-snug">
                {cateringService.description}
              </p>

              <div className="flex flex-wrap items-center gap-3 md:gap-6 mb-4 md:mb-6">
                <div className="flex items-center">
                  {cateringService.rating > 0 ? (
                    <>
                      <Star size={16} className="md:w-5 md:h-5 text-namke-mustard mr-1 md:mr-2 fill-current" />
                      <span className="text-sm md:text-base lg:text-lg font-semibold text-namke-black">
                        {cateringService.rating.toFixed(1)}
                      </span>
                      <span className="text-xs md:text-sm text-namke-black/55 ml-1">{t('cateringServiceDetails.reviewCountParens', { count: cateringService.reviewCount })}</span>
                    </>
                  ) : (
                    <span className="text-xs md:text-sm font-semibold text-[#054625] bg-[#054625]/5 px-2 py-1 rounded-full">
                      {t('cateringServiceDetails.new')}
                    </span>
                  )}
                </div>
                <div className="flex items-center">
                  <Award size={16} className="md:w-5 md:h-5 text-namke-black/45 mr-1 md:mr-2" />
                  <span className="text-xs md:text-sm md:text-base text-namke-black/65">{cateringService.experience}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 md:gap-3">
                <button
                  onClick={() => openQuoteModal()}
                  className="bg-[#054625] hover:bg-[#04381f] text-white px-4 py-2 md:px-6 md:py-3 rounded-full text-sm md:text-base font-semibold transition-colors shadow-[0_10px_24px_rgba(5,70,37,0.22)]"
                >
                  {t('cateringServiceDetails.requestQuote')}
                </button>
                <button
                  onClick={toggleFavorite}
                  disabled={favoriteLoading}
                  className={`p-3 rounded-full transition-colors ${
                    isFavorite
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-namke-cream text-namke-black/70 hover:bg-namke-cream/70'
                  } ${favoriteLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {favoriteLoading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                  <Heart size={20} className={isFavorite ? 'fill-current' : ''} />
                  )}
                </button>
                <button
                  onClick={handleShare}
                  className="bg-namke-cream text-namke-black/70 p-3 rounded-full hover:bg-namke-cream/70 transition-colors"
                  title={t('cateringServiceDetails.share')}
                >
                  <Share2 size={20} />
                </button>
              </div>
            </div>

            <div className="relative">
              {cateringService.images?.length > 1 && (
                <div className="mb-4 grid grid-cols-2 gap-2 rounded-2xl overflow-hidden">
                  {cateringService.images.slice(1, 5).map((src, idx) => (
                    <img
                      key={`${src}-${idx}`}
                      src={src}
                      alt=""
                      className="w-full h-24 sm:h-28 object-cover"
                    />
                  ))}
                </div>
              )}
              <div className="bg-namke-cream/60 rounded-2xl md:rounded-3xl p-4 md:p-6">
                <h3 className="text-base md:text-lg lg:text-xl font-semibold mb-3 md:mb-4 text-namke-black">{t('cateringServiceDetails.quickInfo')}</h3>
                <div className="space-y-2 md:space-y-3">
                  <div className="flex items-center text-namke-black/70">
                    <MapPin size={18} className="text-namke-black/45 mr-3" />
                    <span>{cateringService.location}</span>
                  </div>
                  <div className="flex items-center text-namke-black/70">
                    <Users size={18} className="text-namke-black/45 mr-3" />
                    <span>{t('cateringServiceDetails.capacityRange', { min: cateringService.capacity.min, max: cateringService.capacity.max })}</span>
                  </div>
                  <div className="flex items-center text-namke-black/70">
                    <DollarSign size={18} className="text-namke-black/45 mr-3" />
                    <span>{t(cateringService.pricing.startingPrice.perPerson ? 'cateringServiceDetails.priceFrom' : 'cateringServiceDetails.priceFromPackage', { price: cateringService.pricing.startingPrice.amount, currency: cateringService.pricing.currency })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation des onglets */}
      <div className="bg-white border-b">
        <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
          <nav className="flex space-x-2 md:space-x-8 overflow-x-auto">
            {[
              { id: 'overview', label: t('cateringServiceDetails.tabs.overview'), icon: ChefHat },
              { id: 'menus', label: t('cateringServiceDetails.tabs.menus'), icon: Utensils },
              { id: 'dishes', label: t('cateringServiceDetails.tabs.dishes'), icon: LayoutGrid },
              { id: 'services', label: t('cateringServiceDetails.tabs.services'), icon: CheckCircle },
              { id: 'reviews', label: t('cateringServiceDetails.tabs.reviews'), icon: Star },
              { id: 'contact', label: t('cateringServiceDetails.tabs.contact'), icon: MessageCircle }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 md:py-4 px-2 md:px-3 border-b-2 text-xs sm:text-sm md:text-base font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[#054625] text-[#054625]'
                      : 'border-transparent text-namke-black/60 hover:text-namke-black hover:border-namke-black/20'
                  }`}
                >
                  <Icon size={16} className="md:w-5 md:h-5 inline mr-1 md:mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-8">
        {/* Onglet Aperçu */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-3 gap-4 md:gap-8">
            <div className="md:col-span-2">
              <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6" style={{ color: '#002b11' }}>{t('cateringServiceDetails.aboutOurService')}</h2>
              <p className="text-namke-black/70 mb-6 leading-relaxed">
                {cateringService.description}
              </p>

              <div className="bg-white rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002b11' }}>{t('cateringServiceDetails.ourSpecialties')}</h3>
                <div className="flex flex-wrap gap-2">
                  {cateringService.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="bg-[#054625]/10 text-[#054625] px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002b11' }}>{t('cateringServiceDetails.eventTypesTitle')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {cateringService.eventTypes.map((eventType, index) => (
                    <div key={index} className="flex items-center text-namke-black/70">
                      <CheckCircle size={16} className="text-[#054625] mr-2" />
                      {eventType}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002b11' }}>{t('cateringServiceDetails.capacity')}</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-namke-black/55">{t('cateringServiceDetails.minimum')}</span>
                    <span className="font-semibold text-namke-black">{t('cateringServiceDetails.peopleCount', { count: cateringService.capacity.min })}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-namke-black/55">{t('cateringServiceDetails.maximum')}</span>
                    <span className="font-semibold text-namke-black">{t('cateringServiceDetails.peopleCount', { count: cateringService.capacity.max })}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002b11' }}>{t('cateringServiceDetails.certifications')}</h3>
                {cateringService.certifications.length > 0 ? (
                  <div className="space-y-2">
                    {cateringService.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center text-namke-black/70">
                        <Award size={16} className="text-[#054625] mr-2" />
                        {cert}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-namke-black/50 italic text-sm">{t('cateringServiceDetails.noCertifications')}</p>
                )}
              </div>

            </div>
          </div>
        )}

        {/* Onglet Menus */}
        {activeTab === 'menus' && (
          <div>
            <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6" style={{ color: '#002b11' }}>{t('cateringServiceDetails.ourCateringMenus')}</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {cateringService.menus.map((menu) => (
                <div
                  key={menu.id}
                  className={`bg-white rounded-2xl md:rounded-3xl p-4 md:p-6 border-2 transition-all cursor-pointer ${
                    selectedMenu?.id === menu.id
                      ? 'border-[#054625] shadow-lg'
                      : 'border-namke-black/10 hover:border-[#054625]'
                  }`}
                  onClick={() => setSelectedMenu(menu)}
                >
                  {menu.image_url ? (
                    <div className="w-full h-40 mb-4 rounded-2xl overflow-hidden bg-namke-cream -mx-0">
                      <img
                        src={menu.image_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : null}
                  <div className="text-center mb-3 md:mb-4">
                    <h3 className="text-lg md:text-xl font-semibold mb-2" style={{ color: '#002b11' }}>
                      {menu.name}
                    </h3>
                    <div className="text-2xl md:text-3xl font-bold text-[#054625] mb-1 md:mb-2">
                      {menu.price}{cateringService.pricing.currency}
                    </div>
                    <div className="text-xs md:text-sm text-namke-black/50">{menu.perPerson ? t('cateringServiceDetails.perPerson') : t('cateringServiceDetails.packagePrice')}</div>
                  </div>

                  <p className="text-namke-black/60 text-center mb-4">{menu.description}</p>

                  <div className="space-y-2">
                    {menu.courses.map((course, index) => (
                      <div key={index} className="flex items-center text-namke-black/70">
                        <CheckCircle size={16} className="text-[#054625] mr-2 flex-shrink-0" />
                        <span className="text-sm">{course}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openQuoteModal(menu.id);
                    }}
                    className="w-full mt-4 bg-[#054625] hover:bg-[#04381f] text-white py-2 px-4 rounded-full font-medium transition-colors"
                  >
                    {t('cateringServiceDetails.chooseThisMenu')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Onglet Plats (carte du restaurant) */}
        {activeTab === 'dishes' && (
          <div>
            <h2 className="text-xl md:text-2xl font-bold mb-2 md:mb-4" style={{ color: '#002b11' }}>
              {t('cateringServiceDetails.availableDishes')}
            </h2>
            <p className="text-namke-black/55 mb-6 text-sm md:text-base max-w-3xl">
              {t('cateringServiceDetails.dishesSelectionHint')}
            </p>
            {cateringService.dishes && cateringService.dishes.length > 0 ? (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {cateringService.dishes.map((dish) => (
                  <button
                    key={dish.id}
                    type="button"
                    onClick={() => setSelectedDish(dish)}
                    className="text-left bg-white rounded-2xl overflow-hidden border-2 border-transparent hover:border-[#054625] transition-all group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#054625]"
                  >
                    <div className="aspect-[4/3] bg-namke-cream overflow-hidden">
                      {dish.imageUrl ? (
                        <img
                          src={dish.imageUrl}
                          alt={dish.name}
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-namke-black/35 text-sm px-2 text-center">
                          {t('cateringServiceDetails.photoComingSoon')}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold line-clamp-2" style={{ color: '#002b11' }}>
                        {dish.name}
                      </h3>
                      {dish.description ? (
                        <p className="text-sm text-namke-black/55 mt-1 line-clamp-2">{dish.description}</p>
                      ) : null}
                      <p className="text-lg font-bold mt-2" style={{ color: 'rgb(5,70,37)' }}>
                        {Number(dish.price).toFixed(2)} €
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl">
                <p className="text-namke-black/55">{t('cateringServiceDetails.noDishesPublished')}</p>
              </div>
            )}
          </div>
        )}

        {/* Popup détail plat */}
        {selectedDish && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={() => setSelectedDish(null)}
          >
            <div
              className="bg-white rounded-3xl shadow-xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image */}
              {selectedDish.imageUrl ? (
                <div className="aspect-video w-full bg-namke-cream overflow-hidden">
                  <img
                    src={selectedDish.imageUrl}
                    alt={selectedDish.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-video w-full bg-namke-cream flex items-center justify-center text-namke-black/35 text-sm">
                  {t('cateringServiceDetails.photoComingSoon')}
                </div>
              )}

              {/* Contenu */}
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="text-xl font-bold leading-tight" style={{ color: '#002b11' }}>
                    {selectedDish.name}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setSelectedDish(null)}
                    className="flex-shrink-0 p-1.5 rounded-full hover:bg-namke-cream transition-colors text-namke-black/40 hover:text-namke-black"
                    aria-label={t('cateringServiceDetails.close')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="w-5 h-5">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {selectedDish.description && (
                  <p className="text-namke-black/60 text-sm leading-relaxed mb-4">
                    {selectedDish.description}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-2xl font-extrabold" style={{ color: 'rgb(5,70,37)' }}>
                    {Number(selectedDish.price).toFixed(2)} €
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedDish(null); navigate(`/product/${selectedDish.id}`); }}
                    className="px-4 py-2 rounded-full text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: 'rgb(5,70,37)' }}
                  >
                    {t('cateringServiceDetails.viewFullSheet')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Services */}
        {activeTab === 'services' && (
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#002b11' }}>{t('cateringServiceDetails.includedServices')}</h2>
              <div className="space-y-4">
                {cateringService.pricing.includes.map((service, index) => (
                  <div key={index} className="flex items-center text-namke-black/70">
                    <CheckCircle size={20} className="text-[#054625] mr-3" />
                    <span>{service}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#002b11' }}>{t('cateringServiceDetails.optionalServices')}</h2>
              {cateringService.pricing.extras && cateringService.pricing.extras.length > 0 ? (
              <div className="space-y-4">
                {cateringService.pricing.extras.map((service, index) => (
                  <div key={index} className="flex items-center text-namke-black/70">
                    <ClockIcon size={20} className="text-namke-black/35 mr-3" />
                    <span>{service}</span>
                  </div>
                ))}
              </div>
              ) : (
                <p className="text-namke-black/50 italic">{t('cateringServiceDetails.noOptionalServices')}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#002b11' }}>{t('cateringServiceDetails.serviceDetails')}</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#002b11' }}>{t('cateringServiceDetails.logistics')}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-namke-black/55">{t('cateringServiceDetails.setupTime')}</span>
                      <span className="font-semibold text-namke-black">{cateringService.services.setupTime}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-namke-black/55">{t('cateringServiceDetails.staffIncluded')}</span>
                      <span className="font-semibold text-namke-black">{cateringService.services.staffIncluded ? t('cateringServiceDetails.yes') : t('cateringServiceDetails.no')}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#002b11' }}>{t('cateringServiceDetails.equipment')}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-namke-black/55">{t('cateringServiceDetails.equipmentProvided')}</span>
                      <span className="font-semibold text-namke-black">{cateringService.services.equipmentProvided ? t('cateringServiceDetails.yes') : t('cateringServiceDetails.no')}</span>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-namke-black/55 shrink-0">{t('cateringServiceDetails.dietaryOptions')}</span>
                      <span className="font-semibold text-namke-black text-right">
                        {cateringService.services.dietaryAccommodations.length > 0
                          ? cateringService.services.dietaryAccommodations.join(', ')
                          : t('cateringServiceDetails.notProvided')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Avis */}
        {activeTab === 'reviews' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ color: '#002b11' }}>{t('cateringServiceDetails.customerReviews')}</h2>
              <div className="flex items-center gap-4">
              <div className="flex items-center">
                {cateringService.rating > 0 ? (
                  <>
                    <Star size={20} className="text-namke-mustard mr-2 fill-current" />
                    <span className="text-lg font-semibold text-namke-black">{cateringService.rating.toFixed(1)}</span>
                    <span className="text-namke-black/50 ml-1">/ 5</span>
                  </>
                ) : (
                  <span className="text-sm font-semibold text-[#054625] bg-[#054625]/5 px-3 py-1.5 rounded-full">
                    {t('cateringServiceDetails.newNoReviewsYet')}
                  </span>
                )}
                </div>
                <button
                  onClick={async () => {
                    if (!isAuthenticated) {
                      // Si non connecté, marquer qu'on doit ouvrir le formulaire après connexion
                      setShouldOpenReviewFormAfterLogin(true);
                      // Ouvrir la popup de connexion via handleReviewAction
                      await handleReviewAction('openReviewForm', () => {
                        setShowReviewForm(true);
                      });
                    } else {
                      // Si connecté, ouvrir/fermer le formulaire directement
                      setShowReviewForm(!showReviewForm);
                    }
                  }}
                  className="bg-[#054625] hover:bg-[#04381f] text-white px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2"
                >
                  <MessageCircle size={18} />
                  {showReviewForm ? t('cateringServiceDetails.cancel') : t('cateringServiceDetails.leaveAReview')}
                </button>
              </div>
            </div>

            {/* Formulaire d'avis */}
            {showReviewForm && (
              <div className="bg-white rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002b11' }}>
                  {t('cateringServiceDetails.yourReview')}
                </h3>
                <form onSubmit={handleReviewSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-namke-black/70 mb-2">
                      {t('cateringServiceDetails.rating')}
                    </label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => handleRatingChange(rating)}
                          className={`transition-colors ${
                            rating <= reviewForm.rating
                              ? 'text-namke-mustard'
                              : 'text-namke-black/20'
                          }`}
                        >
                          <Star
                            size={28}
                            className={rating <= reviewForm.rating ? 'fill-current' : ''}
                          />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-namke-black/55">
                        {reviewForm.rating} / 5
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-namke-black/70 mb-2">
                      {t('cateringServiceDetails.yourComment')}
                    </label>
                    <textarea
                      name="review_text"
                      value={reviewForm.review_text}
                      onChange={handleReviewInputChange}
                      rows={4}
                      required
                      className="w-full bg-namke-cream/50 border border-transparent rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#054625] focus:border-transparent"
                      placeholder={t('cateringServiceDetails.shareYourExperience')}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowReviewForm(false);
                        setReviewForm({ rating: 5, review_text: '' });
                      }}
                      className="flex-1 bg-namke-cream text-namke-black py-2 px-4 rounded-full font-medium hover:bg-namke-cream/70 transition-colors"
                    >
                      {t('cateringServiceDetails.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={submittingReview}
                      className="flex-1 bg-[#054625] hover:bg-[#04381f] text-white py-2 px-4 rounded-full font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingReview ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          {t('cateringServiceDetails.sending')}
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          {t('cateringServiceDetails.publishReview')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cateringService.reviews.length > 0 ? (
                cateringService.reviews.map((review) => (
                <div key={review.id} className="bg-white rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-[#054625]/10 flex items-center justify-center">
                          <Users size={20} className="text-[#054625]" />
                        </div>
                    <h3 className="font-semibold text-namke-black">{review.author}</h3>
                      </div>
                    <div className="flex items-center">
                      {Array.from({ length: 5 }, (_, i) => (
                        <Star
                          key={i}
                          size={16}
                          className={`${
                            i < review.rating ? 'text-namke-mustard fill-current' : 'text-namke-black/15'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-namke-black/65 text-sm mb-3">{review.comment}</p>
                  <div className="text-xs text-namke-black/45">{review.date}</div>
                </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <MessageCircle size={48} className="mx-auto text-namke-black/15 mb-4" />
                  <p className="text-namke-black/55">{t('cateringServiceDetails.noReviewsYet')}</p>
                  <p className="text-sm text-namke-black/45 mt-2">{t('cateringServiceDetails.beTheFirstToReview')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onglet Contact */}
        {activeTab === 'contact' && (
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#002b11' }}>{t('cateringServiceDetails.contactInfo')}</h2>
              <div className="space-y-4">
                <div className="flex items-center text-namke-black/70">
                  <MapPin size={20} className="text-[#054625] mr-3" />
                  <div>
                    <div className="font-medium text-namke-black">{cateringService.location}</div>
                    <div className="text-sm text-namke-black/50">{cateringService.address}</div>
                  </div>
                </div>

                <div className="flex items-center text-namke-black/70">
                  <Phone size={20} className="text-[#054625] mr-3" />
                  <div>
                    <div className="font-medium text-namke-black">{t('cateringServiceDetails.phone')}</div>
                    <div className="text-sm text-namke-black/50">{cateringService.phone}</div>
                  </div>
                </div>

                <div className="flex items-center text-namke-black/70">
                  <Mail size={20} className="text-[#054625] mr-3" />
                  <div>
                    <div className="font-medium text-namke-black">{t('cateringServiceDetails.email')}</div>
                    <div className="text-sm text-namke-black/50">{cateringService.email}</div>
                  </div>
                </div>

                <div className="flex items-center text-namke-black/70">
                  <UsersIcon size={20} className="text-[#054625] mr-3" />
                  <div>
                    <div className="font-medium text-namke-black">{t('cateringServiceDetails.website')}</div>
                    <div className="text-sm text-namke-black/50">{cateringService.website}</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#002b11' }}>{t('cateringServiceDetails.requestQuote')}</h2>
              <button
                onClick={() => openQuoteModal()}
                className="w-full bg-[#054625] hover:bg-[#04381f] text-white py-3 px-6 rounded-full font-semibold transition-colors shadow-[0_10px_24px_rgba(5,70,37,0.22)]"
              >
                {t('cateringServiceDetails.openContactForm')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de contact */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[95vh] overflow-y-auto">
            <div className="p-5 md:p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-xl font-semibold" style={{ color: '#002b11' }}>
                  {t('cateringServiceDetails.requestQuote')}
                </h3>
                <button onClick={closeContactModal} className="text-namke-black/35 hover:text-namke-black">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Success screen */}
              {contactSuccess ? (
                <div className="text-center space-y-5 py-4">
                  <div className="w-16 h-16 rounded-full bg-[#054625]/10 flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-[#054625]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-namke-black">{t('cateringServiceDetails.requestSent')}</h4>
                    <p className="text-namke-black/55 text-sm mt-1">
                      {t('cateringServiceDetails.requestReceivedHint')}
                    </p>
                  </div>
                  <div className="bg-namke-cream/60 rounded-2xl p-4 text-left text-sm text-namke-black/70 space-y-1">
                    <p className="font-semibold text-namke-black">{t('cateringServiceDetails.nextSteps')}</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>{t('cateringServiceDetails.stepContact')}</li>
                      <li>{t('cateringServiceDetails.stepDetailedQuote')}</li>
                      <li>{t('cateringServiceDetails.stepDeposit', { percent: Number(selectedOffer?.deposit_percentage ?? cateringService.depositPercentage) })}</li>
                    </ul>
                  </div>
                  <button
                    onClick={closeContactModal}
                    className="w-full bg-[#054625] text-white py-2.5 rounded-full font-semibold hover:bg-[#04381f] transition-colors"
                  >
                    {t('cateringServiceDetails.close')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  {contactError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-3 py-2 text-sm">
                      {contactError}
                    </div>
                  )}

                  {/* Offre */}
                  {cateringServices.length > 1 && (
                    <div>
                      <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.chosenOffer')} *</label>
                      <select
                        value={selectedOffer?.id ?? ''}
                        onChange={(e) => setContactForm((f) => ({ ...f, serviceId: Number(e.target.value) }))}
                        className="w-full bg-namke-cream/50 border border-transparent rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625]"
                      >
                        {cateringServices.map((s) => {
                          const { amount, perPerson } = getOfferStartingPrice(s);
                          return (
                            <option key={s.id} value={s.id}>
                              {s.service_name} — {formatPrice(amount)} {perPerson ? t('cateringServiceDetails.perPerson') : t('cateringServiceDetails.packagePrice')}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}

                  {/* Coordonnees */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.email')} *</label>
                      <input
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-namke-cream/50 border border-transparent rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.phone')}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={contactForm.phone}
                        onChange={handleInputChange}
                        placeholder="+33 6 ..."
                        className="w-full bg-namke-cream/50 border border-transparent rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625]"
                      />
                    </div>
                  </div>

                  {/* Type d'evenement */}
                  <div>
                    <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.eventType')} *</label>
                    <select
                      name="occasion"
                      value={contactForm.occasion}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-namke-cream/50 border border-transparent rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625]"
                    >
                      <option value="">{t('cateringServiceDetails.selectEventType')}</option>
                      {CATERING_OCCASIONS.map((opt) => (
                        <option key={opt.id} value={opt.id}>{t(`catering.quote.eventTypes.${opt.id}`)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Date + Heure */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.date')} *</label>
                      <input
                        type="date"
                        name="date"
                        value={contactForm.date}
                        onChange={handleInputChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full bg-namke-cream/50 border border-transparent rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.time')}</label>
                      <input
                        type="time"
                        name="time"
                        value={contactForm.time}
                        onChange={handleInputChange}
                        className="w-full bg-namke-cream/50 border border-transparent rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625]"
                      />
                    </div>
                  </div>

                  {/* Invites + Lieu */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.guestCount')} *</label>
                      <input
                        type="number"
                        name="guests"
                        value={contactForm.guests}
                        onChange={handleInputChange}
                        required
                        min={selectedOffer?.min_guests || 1}
                        max={selectedOffer?.max_guests || undefined}
                        placeholder={`${selectedOffer?.min_guests || 1}-${selectedOffer?.max_guests || '∞'}`}
                        className="w-full bg-namke-cream/50 border border-transparent rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.location')}</label>
                      <input
                        type="text"
                        name="location"
                        value={contactForm.location}
                        onChange={handleInputChange}
                        placeholder={t('cateringServiceDetails.cityOrAddress')}
                        className="w-full bg-namke-cream/50 border border-transparent rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625]"
                      />
                    </div>
                  </div>

                  {selectedOffer && (
                    <p className="text-xs text-namke-black/50 -mt-2">
                      {t('cateringServiceDetails.guestsAllowed', { min: selectedOffer.min_guests || 1, max: selectedOffer.max_guests || '∞' })}
                      {selectedOffer.min_notice_hours ? ` · ${t('cateringServiceDetails.minNotice', { hours: selectedOffer.min_notice_hours })}` : ''}
                    </p>
                  )}

                  {/* Options payantes proposées par l'offre (incluses dans le total) */}
                  {selectedOffer && CATERING_PAID_OPTIONS.some((o) => selectedOffer[o.flag]) && (
                    <div>
                      <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.paidOptions')}</label>
                      <div className="space-y-2">
                        {CATERING_PAID_OPTIONS.filter((o) => selectedOffer[o.flag]).map((o) => {
                          const fee = Number(selectedOffer[o.fee] || 0);
                          if (o.perHour) {
                            return (
                              <div key={o.id} className="flex items-center justify-between gap-3 text-sm">
                                <span>{t('cateringServiceDetails.staffHours')} <span className="text-namke-black/50">({formatPrice(fee)}/h)</span></span>
                                <input
                                  type="number"
                                  min={0}
                                  max={24}
                                  value={contactForm.options.staff_hours}
                                  onChange={(e) => setContactOption('staff_hours', Math.max(0, Math.min(24, parseInt(e.target.value, 10) || 0)))}
                                  className="w-20 bg-namke-cream/50 border border-transparent rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625]"
                                />
                              </div>
                            );
                          }
                          return (
                            <label key={o.id} className="flex items-center justify-between gap-3 text-sm cursor-pointer">
                              <span className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={contactForm.options[o.id]}
                                  onChange={(e) => setContactOption(o.id, e.target.checked)}
                                  className="accent-[#054625]"
                                />
                                {t(`cateringServiceDetails.option_${o.id}`)}
                              </span>
                              <span className="text-namke-black/60">{fee > 0 ? `+${formatPrice(fee)}` : t('cateringServiceDetails.included')}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Régimes alimentaires */}
                  <div>
                    <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.dietary')}</label>
                    <div className="flex flex-wrap gap-2">
                      {DIETARY_OPTIONS.map((d) => {
                        const active = contactForm.dietary.includes(d.id);
                        return (
                          <button
                            key={d.id}
                            type="button"
                            onClick={() => toggleContactDietary(d.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${active ? 'bg-[#054625] text-white border-[#054625]' : 'bg-namke-cream/50 border-transparent text-namke-black/70'}`}
                          >
                            {t(`catering.dietaryOptions.${d.id}`)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-namke-black/70 mb-1">{t('cateringServiceDetails.specificNeeds')}</label>
                    <textarea
                      name="message"
                      value={contactForm.message}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full bg-namke-cream/50 border border-transparent rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#054625] resize-none"
                      placeholder={t('cateringServiceDetails.specificNeedsPlaceholder')}
                    />
                  </div>

                  {/* Estimation de prix */}
                  {(() => {
                    const est = getModalEstimate();
                    if (!est) return null;
                    return (
                      <div className="bg-[#054625]/5 border border-[#054625]/20 rounded-2xl p-3 text-sm">
                        <div className="flex justify-between font-semibold text-[#054625]">
                          <span>{t('cateringServiceDetails.totalEstimate')}</span>
                          <span>{formatPrice(est.total)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-namke-terracotta mt-0.5">
                          <span>{t('cateringServiceDetails.depositParens', { percent: est.depositPercentage })}</span>
                          <span>{formatPrice(est.deposit)}</span>
                        </div>
                        <p className="text-xs text-namke-black/50 mt-1">{t('cateringServiceDetails.estimateDisclaimer')}</p>
                      </div>
                    );
                  })()}

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeContactModal}
                      className="flex-1 bg-namke-cream text-namke-black py-2.5 rounded-full font-medium hover:bg-namke-cream/70 transition-colors text-sm"
                    >
                      {t('cateringServiceDetails.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={contactLoading}
                      className="flex-1 bg-[#054625] hover:bg-[#04381f] text-white py-2.5 rounded-full font-medium transition-colors text-sm flex items-center justify-center gap-2 disabled:bg-namke-black/20 disabled:cursor-not-allowed"
                    >
                      {contactLoading ? (
                        <>
                          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          {t('cateringServiceDetails.sending')}
                        </>
                      ) : t('cateringServiceDetails.sendRequest')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Popup de connexion */}
      {showLoginPopup && (
        <LoginPopup
          onClose={() => {
            closeLoginPopup();
            setShouldOpenReviewFormAfterLogin(false);
          }}
          onLoginSuccess={async (userData) => {
            await handleLoginSuccess(userData);
            // Si on devait ouvrir le formulaire après connexion, l'ouvrir maintenant
            if (shouldOpenReviewFormAfterLogin) {
              setShowReviewForm(true);
              setShouldOpenReviewFormAfterLogin(false);
            }
          }}
        />
      )}

      <Footer />
    </div>
  );
};

export default CateringServiceDetails;
